import { create } from 'zustand';

import { KafkaAction, KafkaMetadata } from '@/types/websocket-types';

import { useAuthStore } from '@/stores/authStore';
import { useWebSocketStore } from '@/stores/websocketStore';

import wsWorkerUrl from '@/services/workers/websocket-worker?worker&url';

type PendingRequest<R = any> = {
  resolve: (value: R) => void;
  reject: (reason?: any) => void;
  repeatRequest: () => void;
  timeout?: ReturnType<typeof setTimeout>;
};

export interface WsClientState {
  worker: Worker | null;
  pendingRequests: Map<string, PendingRequest<any>>;
  workerReadyPromise: Promise<void> | null;
  webSocketReady: boolean;

  initWorker: () => Promise<void>;
  initWebSocket: (url: string, token: string) => Promise<void>;
  disconnect: () => void;
  sendRequest: <TData = any, TResponse = any>(
    data: TData,
    metadata: KafkaMetadata,
    timeoutMs?: number,
  ) => Promise<TResponse>;
  handleWorkerMessage: (evt: MessageEvent) => void;
  createMetadata: (method: string, action: KafkaAction) => KafkaMetadata;

  create: <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData,
    extra?: Record<string, unknown>,
  ) => Promise<TResponse>;
  read: <TResponse = unknown>(
    method: string,
    uuid: string,
  ) => Promise<TResponse>;
  update: <TData = unknown, TResponse = unknown>(
    method: string,
    uuid: string,
    data: TData,
  ) => Promise<TResponse>;
  delete: <TResponse = void>(
    method: string,
    uuid: string,
  ) => Promise<TResponse>;
  list: <TResponse = unknown>(
    method: string,
    uuids?: string[],
    extra?: Record<string, unknown>,
  ) => Promise<TResponse[]>;
  bulkCreate: <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData[],
  ) => Promise<TResponse[]>;
  bulkUpdate: <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData[],
  ) => Promise<TResponse[]>;
  bulkDelete: <TResponse = unknown>(
    method: string,
    uuids: string[],
  ) => Promise<TResponse[]>;
}

export const useWsClientStore = create<WsClientState>((set, get) => ({
  worker: null,
  pendingRequests: new Map<string, PendingRequest<any>>(),
  workerReadyPromise: null,
  webSocketReady: false,

  initWorker: async () => {
    const { workerReadyPromise, handleWorkerMessage } = get();
    if (workerReadyPromise) {
      return workerReadyPromise;
    }

    const promise = new Promise<void>((resolve, reject) => {
      try {
        if (typeof window === 'undefined') {
          return resolve();
        }

        const worker = new Worker(wsWorkerUrl, { type: 'module' });
        set({ worker });

        const initTimeout = setTimeout(() => {
          reject(new Error('Worker initialization timeout'));
        }, 5000);

        const handleInit = (evt: MessageEvent) => {
          if (evt.data?.type === 'WORKER_READY') {
            clearTimeout(initTimeout);
            worker.removeEventListener('message', handleInit);
            resolve();
          }
        };

        worker.addEventListener('message', handleInit);
        worker.onmessage = handleWorkerMessage;

        worker.postMessage({ command: 'INIT_WORKER' });
      } catch (error) {
        console.error('Worker initialization error:', error);
        reject(error);
      }
    });

    set({ workerReadyPromise: promise });
    return promise;
  },

  handleWorkerMessage: (evt: MessageEvent) => {
    const { type, data } = evt.data;
    const { pendingRequests } = get();

    switch (type) {
      case 'RESPONSE': {
        const requestId = data?.metadata?.request_id;
        if (!requestId) return;

        const pending = pendingRequests.get(requestId);
        if (pending) {
          const { resolve, timeout } = pending;
          if (timeout) clearTimeout(timeout);
          pendingRequests.delete(requestId);
          resolve(data.data);
        }
        break;
      }

      case 'ERROR':
        if (data?.reason === 'WebSocket not connected.') {
          useWebSocketStore.getState().reconnect();
          return;
        }
        useWebSocketStore.getState().reconnect();
        break;

      case 'EXPIRED':
        useWebSocketStore.getState().setIsConnected(false);
        useAuthStore.getState().refreshAccessToken();
        break;

      case 'CLOSE':
        set({ webSocketReady: false });
        break;

      case 'OPEN':
        set({ webSocketReady: true });
        // Retry all pending requests when connection is established
        pendingRequests.forEach((request) => {
          request.repeatRequest();
        });
        break;
      case 'WORKER_READY':
        break;

      default:
        break;
    }
  },

  initWebSocket: async (url: string, token: string) => {
    const { worker } = get();
    if (!worker) {
      await get().initWorker();
    }

    const connectionPromise = new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 5000);

      const handleConnection = (evt: MessageEvent) => {
        const { type, reason } = evt.data;
        if (type === 'OPEN') {
          clearTimeout(timeout);
          get().worker?.removeEventListener('message', handleConnection);
          resolve();
        } else if (type === 'ERROR') {
          clearTimeout(timeout);
          get().worker?.removeEventListener('message', handleConnection);
          reject(new Error(reason || 'Failed to connect'));
        }
      };

      get().worker?.addEventListener('message', handleConnection);
    });

    get().worker?.postMessage({ command: 'INIT', payload: { url, token } });

    await connectionPromise;
  },

  disconnect: () => {
    const { worker } = get();
    if (worker) {
      try {
        worker.postMessage({ command: 'DISCONNECT' });
        worker.onmessage = null;
        worker.onerror = null;
        worker.terminate();
      } catch (error) {
        console.error('Error disconnecting worker:', error);
      }
    }
    set({ worker: null, workerReadyPromise: null, webSocketReady: false });
  },

  sendRequest: async <TData = any, TResponse = any>(
    data: TData,
    metadata: KafkaMetadata,
    timeoutMs = 1200000,
  ) => {
    if (!get().worker) {
      await get().initWorker();
    }

    const worker = get().worker;
    const { pendingRequests, webSocketReady } = get();

    if (!worker) {
      throw new Error('WebSocket worker not initialized');
    }

    const requestId = crypto.randomUUID();

    return new Promise<TResponse>((resolve, reject) => {
      const timeout = setTimeout(() => {
        pendingRequests.delete(requestId);
        reject(
          new Error(
            `Request timed out after ${timeoutMs}ms for request ${requestId}`,
          ),
        );
      }, timeoutMs);

      const repeatRequest = () => {
        worker.postMessage({
          command: 'SEND',
          payload: {
            requestId,
            data,
            metadata: { ...metadata, request_id: requestId },
          },
        });
      };

      pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
        repeatRequest,
      });

      // Only send the request immediately if websocket is connected
      if (webSocketReady) {
        repeatRequest();
      } else {
        // If not connected, the request will be automatically sent when connection is established
        console.log(
          `WebSocket not connected. Request ${requestId} will be sent when connected.`,
        );
        // Initiate connection if needed
        useWebSocketStore.getState().reconnect();
      }
    });
  },

  createMetadata: (method: string, action: KafkaAction) => {
    return {
      topic: 'eh-requests-photoarchive-dev',
      action,
      method,
      request_id: '',
      extra: {},
    };
  },

  create: async <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData,
    extra?: Record<string, unknown>,
  ) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<TData, TResponse>(data, {
      ...createMetadata(method, 'create'),
      ...(extra ? { extra } : {}),
    });
  },

  bulkCreate: async <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData[],
  ) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<TData[], TResponse[]>(
      data,
      createMetadata(method, 'bulk_create'),
    );
  },

  read: async <TResponse = unknown>(method: string, uuid: string) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<{ uuid: string }, TResponse>(
      { uuid },
      createMetadata(method, 'read'),
    );
  },

  update: async <TData = unknown, TResponse = unknown>(
    method: string,
    uuid: string,
    data: TData,
  ) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<TData & { uuid: string }, TResponse>(
      { ...data, uuid },
      createMetadata(method, 'update'),
    );
  },

  bulkUpdate: async <TData = unknown, TResponse = unknown>(
    method: string,
    data: TData[],
  ) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<TData[], TResponse[]>(
      data,
      createMetadata(method, 'bulk_update'),
    );
  },

  delete: async <TResponse = void>(method: string, uuid: string) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<{ uuid: string }, TResponse>(
      { uuid },
      createMetadata(method, 'delete'),
    );
  },

  bulkDelete: async <TResponse = void>(method: string, uuids: string[]) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<{ uuids: string[] }, TResponse[]>(
      { uuids },
      createMetadata(method, 'bulk_delete'),
    );
  },

  list: async <TResponse = unknown>(
    method: string,
    uuids?: string[],
    extra?: Record<string, unknown>,
  ) => {
    const { sendRequest, createMetadata } = get();
    return sendRequest<string[] | undefined, TResponse[]>(uuids, {
      ...createMetadata(method, 'list'),
      ...(extra ? { extra } : {}),
    });
  },
}));
